import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { triggerTracking } from '@zoopla/tracking';

import { useAuthSteps } from './Components/Stepper/useAuthSteps';
import { AuthStepper } from './Components/Stepper';
import { StepProps, AuthModalProps, StepsObject } from './AuthModal.types';
import {
    ResultButtonProps,
    YourSituationStepProps
} from './Components/YourSituationStep';
import { AuthStep } from './Components/AuthStep';
import { UserWithNextBestAction } from './hooks/authMutationHook.types';
import {
    SituationQuickAccessStepProps,
    eventCategory as situationQuickAccessCategory
} from './Components/SituationQuickAccessStep';
import { ChangePasswordStepProps } from './Components/ChangePasswordStep';
import { SignInFormData } from './Components/SigninForm/SignInForm';

const signInModalHeadingTn = 'Save properties';

const LazyPreferencesStep = dynamic<StepProps>(
    () =>
        import('./Components/PreferencesStep').then(
            (mod) => mod.PreferencesStep
        ),
    { ssr: false }
);

const LazyYourSituationStep = dynamic<YourSituationStepProps>(
    () =>
        import('./Components/YourSituationStep').then(
            (mod) => mod.YourSituationStep
        ),
    {
        ssr: false
    }
);

const LazyYourSituationQuestionResultButton = dynamic<ResultButtonProps>(
    () =>
        import('./Components/YourSituationStep').then(
            (mod) => mod.YourSituationResult.Button
        ),
    {
        ssr: false
    }
);

const LazySituationQuickAccessStep = dynamic<SituationQuickAccessStepProps>(
    () =>
        import('./Components/SituationQuickAccessStep').then(
            (mod) => mod.SituationQuickAccessStep
        ),
    {
        ssr: false
    }
);

const LazyChangePasswordStep = dynamic<ChangePasswordStepProps>(
    () =>
        import('./Components/ChangePasswordStep').then(
            (mod) => mod.ChangePasswordStep
        ),
    {
        ssr: false
    }
);

// this is a const otherwise this goes into an infinite re-rendering
const defaultInitialStepsOrder = ['auth', 'onboarding'];

export const AuthModal = <
    AdditionalStepsNames extends string | undefined = undefined
>({
    initialMode = 'register',
    prefilledEmail,
    modalAuthHeading,
    trackingData,
    isShowing,
    close,
    actionAfterAuth,
    additionalSteps,
    additionalStepsTitlesMap,
    initialStepsOrder = defaultInitialStepsOrder,
    quickAccessPageType
}: AuthModalProps<AdditionalStepsNames>) => {
    const {
        currentStep,
        yourSituationStepData: { title: _, ...yourSituationStepData },
        enqueueOnboardingAndMoveSteps,
        moveToNextStep,
        resetSteps,
        enqueueWeakPasswordAndMoveSteps,
        enqueueOnboardingRemoveAuthAndMoveSteps,
        signInData: { email, password }
    } = useAuthSteps({
        onDone: () => close(hasAuthenticated.current),
        stepsOrder: initialStepsOrder
    });

    const overrideInitialMode = useRef<'register' | 'sign-in'>();
    const hasAuthenticated = useRef(false);

    const onAuthentication = async (
        user: UserWithNextBestAction,
        weakPasswordUserInput?: SignInFormData
    ) => {
        if (weakPasswordUserInput?.email) {
            enqueueWeakPasswordAndMoveSteps(weakPasswordUserInput);
            return;
        }

        hasAuthenticated.current = true;

        await actionAfterAuth?.();

        await enqueueOnboardingAndMoveSteps(user);
    };

    const onPasswordChangeComplete = async () => {
        hasAuthenticated.current = true;
        await actionAfterAuth?.();
    };

    const onWeakPasswordCompleted = async (user: UserWithNextBestAction) => {
        await enqueueOnboardingAndMoveSteps(user);
    };

    const onQuickAccessAuthentication = async (
        user: UserWithNextBestAction
    ) => {
        hasAuthenticated.current = true;
        await actionAfterAuth?.();
        await enqueueOnboardingRemoveAuthAndMoveSteps(user);
    };

    const stepsMap: StepsObject<string> = {
        auth: {
            title: modalAuthHeading || signInModalHeadingTn,
            trackingCategory:
                initialMode === 'sign-in' ? 'signin' : 'registration'
        },
        preferences: {
            title: 'Get expert advice, guidance and inspiration straight to your inbox.',
            trackingCategory:
                initialMode === 'sign-in' ? 'signin' : 'registration'
        },
        'situation-quick-access': {
            title: 'Customise your experience',
            trackingCategory: situationQuickAccessCategory
        },
        'your-situation': {
            title: 'Customise your experience',
            trackingCategory:
                yourSituationStepData.questionType === 'default'
                    ? 'registration'
                    : 'signin'
        },
        'change-weak-password': {
            title: "We're improving our account security",
            trackingCategory: 'signin'
        },
        ...additionalStepsTitlesMap
    };

    const trackingCategory = stepsMap[currentStep].trackingCategory;

    // This runs when the user clicks the close button, outside the modal or uses the Escape key (not on submit)
    const toggleClosed = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: trackingCategory,
            eventAction: 'form closed',
            eventLabel: `${trackingData?.label}_step_${currentStep}`
        });

        close(hasAuthenticated.current);
    };

    const changeToOtherMode = (newMode: 'register' | 'sign-in') => {
        overrideInitialMode.current = newMode;
    };

    useEffect(() => {
        if (isShowing) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: trackingCategory,
                eventAction: 'form shown',
                eventLabel: trackingData.label
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowing]);

    useEffect(() => {
        if (!isShowing) {
            // Here is waiting for the animation of the modal overlay
            const resetStepTimer = setTimeout(() => {
                resetSteps();
            }, 200);

            return () => {
                clearTimeout(resetStepTimer);
            };
        }

        return () => null;
    }, [isShowing, resetSteps]);

    const title =
        currentStep in stepsMap
            ? stepsMap[currentStep].title
            : modalAuthHeading || signInModalHeadingTn;

    return (
        <ControlledDialog
            isOpen={isShowing}
            title={title}
            label={title}
            onClose={toggleClosed}
        >
            <AuthStepper.Provider value={{ moveToNextStep, currentStep }}>
                <AuthStepper.Step id="auth">
                    <AuthStep
                        initialMode={overrideInitialMode.current || initialMode}
                        onAuthentication={onAuthentication}
                        trackingData={trackingData}
                        prefilledEmail={prefilledEmail}
                    />
                </AuthStepper.Step>
                <AuthStepper.Step id="preferences">
                    <LazyPreferencesStep
                        onStepCompleted={moveToNextStep}
                        trackingData={trackingData}
                    />
                </AuthStepper.Step>
                <AuthStepper.Step id="your-situation">
                    <LazyYourSituationStep
                        trackingData={trackingData}
                        onSkip={moveToNextStep}
                        // this is not called from the YS step, since
                        // the action element takes care of moving to the next step
                        onStepCompleted={() => null}
                        actionElement={
                            <LazyYourSituationQuestionResultButton
                                onClick={moveToNextStep}
                            />
                        }
                        {...yourSituationStepData}
                    />
                </AuthStepper.Step>
                <AuthStepper.Step id="situation-quick-access">
                    <LazySituationQuickAccessStep
                        introText="Sign in or register to save these details and enjoy more from Zoopla."
                        selectMode={changeToOtherMode}
                        pageType={quickAccessPageType}
                        onAuthentication={onQuickAccessAuthentication}
                        onStepCompleted={moveToNextStep}
                        trackingData={trackingData}
                    />
                </AuthStepper.Step>
                <AuthStepper.Step id="change-weak-password">
                    <LazyChangePasswordStep
                        previousAccountData={{ email, password }}
                        onSkip={() => close(false)}
                        onStepCompleted={onWeakPasswordCompleted}
                        onPasswordChangeComplete={onPasswordChangeComplete}
                        trackingData={trackingData}
                    />
                </AuthStepper.Step>
                {additionalSteps}
            </AuthStepper.Provider>
        </ControlledDialog>
    );
};

export default AuthModal;
